import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatLegacyDialog, MatLegacyDialogModule } from '@angular/material/legacy-dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { ReservedRole } from '@core/enums';
import { AuthService } from '@core/services';
import { Store } from '@ngxs/store';
import { SavedViewSidebarComponent } from '@shared/components/saved-view-sidebar/saved-view-sidebar.component';
import { RelatedToType } from '@shared/enums';
import { UserState } from '@state/user/state';
import { TaskState } from '@tasks/state/state';
import { TaskList, TaskViewType } from '@tasks/tasks.types';
import { filter, Observable } from 'rxjs';
import { TS_BADGE_TYPES, TSTreeAction, TSTreeMenuModule, TSTreeNode } from 'tsui';
import { SavedViewsSidebarService } from '@shared/services/saved-views-sidebar.service';

@Component({
  selector: 'app-task-sidebar',
  standalone: true,
  imports: [SavedViewSidebarComponent, TSTreeMenuModule, MatLegacyDialogModule],
  template: `
    <!-- @if (showSavedView) {
      <app-saved-view-sidebar sidenav [relatedToType]="relatedToType.AggregatedTasks"></app-saved-view-sidebar>
    } @else { -->
    <ts-tree-menu title="Task Lists" [items]="tsTreeTaskLists" [suppressHeader]="true"></ts-tree-menu>
    <!-- } -->
  `,
  styleUrl: './task-sidebar.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TaskSidebarComponent implements OnInit {
  private activatedRoute = inject(ActivatedRoute);
  private store = inject(Store);
  private router = inject(Router);
  private authService = inject(AuthService);
  private changeDetectorRef = inject(ChangeDetectorRef);
  private destroyRef = inject(DestroyRef);

  private _matDialog = inject(MatLegacyDialog);
  viewType$: Observable<TaskViewType> = this.store.select(TaskState.getViewType);

  tsTreeTaskLists: TSTreeNode[] = [];
  tsTreeActions: TSTreeAction[] = [];
  relatedToType = RelatedToType;
  isTaskCollaborator;
  taskLists: TaskList[];
  sidebarItems$: Observable<any[]> = this.store
    .select(TaskState.getSidebarItems)
    .pipe(filter((taskLists) => !!taskLists));

  ngOnInit(): void {
    const currentUser = this.store.selectSnapshot(UserState.getUser);
    this.sidebarItems$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((taskLists: any[]) => {
      this.taskLists = taskLists.sort((a, b) => a.rank - b.rank);
      this._generateTaskListsMenuLinks();
      this.changeDetectorRef.detectChanges();
    });

    this.viewType$.subscribe((viewType) => {
      this._generateTaskListsMenuLinks();
      this.changeDetectorRef.markForCheck();
    });

    this.isTaskCollaborator = currentUser.roles.some((role) => role.name === ReservedRole.Collaborator);
  }

  get module(): string | undefined {
    return this.store.selectSnapshot(TaskState.getTaskRelatedTo)?.relatedToType;
  }

  private _generateTaskListsMenuLinks(): void {
    this.tsTreeTaskLists = [];
    const allRoutesBeforeTaskList = this.activatedRoute.pathFromRoot.filter(
      (a) => !Object.keys(a.snapshot.params).includes('taskListId'),
    );
    const url = allRoutesBeforeTaskList
      .map((a) => {
        if (a.snapshot.url.join('') !== '') {
          return a.snapshot.url.join('/');
        }
      })
      .join('/');

    const currentViewType = this.store.selectSnapshot(TaskState.getViewType);
    // Iterate through the folders
    this.taskLists.forEach((taskList) => {
      this.tsTreeTaskLists.push({
        id: taskList.id.toString(),
        name: taskList.name,
        icon: 'list_alt',
        link: `${url}/${taskList.id}/${currentViewType}`.replace(/\/\//g, '/'),
        badge: this.getBadge(taskList),
      });
    });

    this.tsTreeTaskLists.sort(SavedViewsSidebarService.sortSavedViewsTreeNodes);
  }

  private getBadge(taskList: TaskList): { value: string; type: TS_BADGE_TYPES } | undefined {
    if (taskList.complete_task_count == undefined && taskList.task_count == undefined) {
      return undefined;
    }

    const taskCounters = this.store.selectSnapshot(TaskState.getTaskCounters)?.[taskList.id];
    return {
      value: `${taskCounters?.currentTaskCompleteCount}/${taskCounters?.currentTaskCount}`,
      type: 'warning',
    };
  }
}
